import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Download, RefreshCw, FileText, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import apiClient from '@/services/apiClient';

const FGDesignTransferReport = () => {
  const navigate = useNavigate();
  
  // State
  const [transfers, setTransfers] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [stats, setStats] = useState({ today_count: 0, total_count: 0 });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [originalBarcode, setOriginalBarcode] = useState('');
  const [targetBarcode, setTargetBarcode] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const limit = 50;

  useEffect(() => {
    fetchTransfers();
    fetchStats();
  }, [page]);

  const fetchTransfers = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (originalBarcode) params.append('original_barcode', originalBarcode);
      if (targetBarcode) params.append('target_barcode', targetBarcode);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      params.append('skip', page * limit);
      params.append('limit', limit);

      const response = await apiClient.get(`/fg-design-transfer/history?${params}`);
      setTransfers(response.data.transfers || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching transfers:', error);
      toast.error('Failed to load transfer history');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/fg-design-transfer/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchTransfers();
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (originalBarcode) params.append('original_barcode', originalBarcode);
      if (targetBarcode) params.append('target_barcode', targetBarcode);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await apiClient.get(`/fg-design-transfer/history/export?${params}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `design_transfer_report_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="fg-design-transfer-report">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/wms/fg')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display flex items-center gap-2">
                <FileText className="w-6 h-6" />
                Design Transfer Report
              </h1>
              <p className="text-sm text-[#E8DCC8]">View and export transfer history</p>
            </div>
          </div>
          <Button 
            onClick={handleExport}
            disabled={isExporting}
            className="bg-[#2D5F4F] hover:bg-[#234A3D]"
            data-testid="export-btn"
          >
            {isExporting ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export CSV
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-800">{stats.today_count}</p>
              <p className="text-xs text-blue-600">Today's Transfers</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-800">{stats.total_count}</p>
              <p className="text-xs text-green-600">Total Transfers</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-800">{total}</p>
              <p className="text-xs text-purple-600">Filtered Results</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-orange-800">{Math.ceil(total / limit)}</p>
              <p className="text-xs text-orange-600">Total Pages</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-[#E5DFD0]">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <Input
                placeholder="Search USN, Design..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="col-span-2"
              />
              <Input
                placeholder="Original Barcode"
                value={originalBarcode}
                onChange={(e) => setOriginalBarcode(e.target.value)}
              />
              <Input
                placeholder="Target Barcode"
                value={targetBarcode}
                onChange={(e) => setTargetBarcode(e.target.value)}
              />
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="text-sm"
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="text-sm"
              />
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => {
                setSearchTerm('');
                setOriginalBarcode('');
                setTargetBarcode('');
                setDateFrom('');
                setDateTo('');
                setPage(0);
                setTimeout(fetchTransfers, 100);
              }}>
                Clear
              </Button>
              <Button onClick={handleSearch} className="bg-[#2D5F4F] hover:bg-[#234A3D]">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Transfers Table */}
        <Card className="border-[#E5DFD0]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[#1A1F2E]">Transfer History</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Transfer ID</TableHead>
                    <TableHead className="font-semibold">USN Code</TableHead>
                    <TableHead className="font-semibold">From (Design/Shade)</TableHead>
                    <TableHead className="font-semibold">To (Design/Shade)</TableHead>
                    <TableHead className="font-semibold">From Bin</TableHead>
                    <TableHead className="font-semibold">To Bin</TableHead>
                    <TableHead className="font-semibold">Transferred By</TableHead>
                    <TableHead className="font-semibold">Date/Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                      </TableCell>
                    </TableRow>
                  ) : transfers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        <FileText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                        <p>No transfers found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    transfers.map((transfer) => (
                      <TableRow key={transfer.transfer_id} className="hover:bg-gray-50">
                        <TableCell className="font-mono text-xs">{transfer.transfer_id}</TableCell>
                        <TableCell className="font-mono text-sm font-medium">{transfer.usn_code}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium">{transfer.original_design}</p>
                            <p className="text-gray-500 text-xs">{transfer.original_shade}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium text-green-700">{transfer.target_design}</p>
                            <p className="text-green-600 text-xs">{transfer.target_shade}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            {transfer.original_bin || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-800 font-mono text-xs">
                            {transfer.target_bin || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{transfer.transferred_by_name}</TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {formatDate(transfer.transferred_at)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            {/* Pagination */}
            {total > limit && (
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-gray-500">
                  Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(page + 1) * limit >= total}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default FGDesignTransferReport;
